import { Tabs, TabsList, TabsTrigger } from '@bpinternal/ui-kit-next'
import { Link, useSearch } from '@tanstack/react-router'
import { ReactNode } from 'react'
import { cn } from '~/utils'
import { usePortal } from '../providers'
import type { LinkOptions } from '../types/navigation'

type Props = {
  title?: ReactNode
  actions?: ReactNode
  primaryActions?: ReactNode
  justify?: 'start' | 'center' | 'end' | 'between'
  children?: ReactNode
  className?: string
  navbarItems?: NavbarItem[]
  navbarActions?: ReactNode
}

export type NavbarItem = { leading?: ReactNode; trailing?: ReactNode; children: ReactNode; active?: boolean } & Pick<
  LinkOptions,
  'to' | 'from'
>

export const Page = ({
  title,
  actions,
  primaryActions,
  children,
  justify = 'between',
  className,
  navbarItems = [],
  navbarActions,
}: Props) => {
  const { setPageActionsPortalRef } = usePortal()

  const justifyClass = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  }[justify]

  return (
    <div className={cn('mx-auto w-full max-w-screen-2xl', primaryActions && 'pb-20 md:pb-0', className)}>
      {(title || actions || primaryActions || navbarItems.length > 0) && (
        <div className="flex flex-col gap-2 pb-6">
          <div className={cn('flex max-w-full flex-wrap items-center gap-4 md:flex-nowrap', justifyClass)}>
            <div className="min-w-0 max-w-full whitespace-nowrap p-0.5 text-2xl font-bold text-neutral-800">
              {title}
            </div>
            <div className="flex flex-wrap gap-2 md:flex-nowrap">
              <div ref={setPageActionsPortalRef} className="flex gap-2" />
              {actions}
              {primaryActions && <div className="ml-2 hidden md:block">{primaryActions}</div>}
            </div>
          </div>
          {navbarItems.length > 0 && (
            <div className="flex items-center gap-2">
              <Navbar navbarItems={navbarItems} />
              {navbarActions}
            </div>
          )}
        </div>
      )}
      {children}

      {primaryActions && (
        <div className="fixed bottom-0 left-0 right-0 z-50 h-20 bg-gradient-to-t from-background via-background to-transparent p-5 pt-2 md:hidden">
          {primaryActions}
        </div>
      )}
    </div>
  )
}

type NavbarProps = {
  navbarItems: NavbarItem[]
}
const Navbar = ({ navbarItems }: NavbarProps) => {
  const activeValue = navbarItems.find((item) => item.active)?.to || navbarItems[0]?.to
  const searchParams = useSearch({ strict: false })

  return (
    <Tabs value={activeValue} className="h-full">
      <TabsList>
        {navbarItems.map(({ children, leading, trailing, to, from }) => (
          <TabsTrigger asChild value={to} key={to}>
            <Link to={to as any} from={from as any} search={searchParams as any}>
              <div className="flex items-center gap-2">
                <>
                  {leading}
                  {children}
                  {trailing}
                </>
              </div>
            </Link>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
